import { useAuth } from "@/context/AuthContext"
import { Navigate, useLocation } from "react-router-dom"
import { ReactNode } from "react"
import { Loader2 } from "lucide-react"

type Props = {
  children: ReactNode
  allowedRoles?: string[]
  requirePlatformAdmin?: boolean
  requireClinicalProfile?: boolean
}

function getHomeRoute(user: { role: string; doctor_id?: string }): string {
  if (user.doctor_id) return "/";
  if (user.role === "staff") return "/frontdesk";
  if (user.role === "admin") return "/settings";
  return "/";
}

export default function ProtectedRoute({ children, allowedRoles, requirePlatformAdmin, requireClinicalProfile }: Props) {
  const { currentUser, isLoading } = useAuth()
  const location = useLocation()

  // Auth state still being restored on page load/refresh — avoid bouncing to /login
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  // Not authenticated - redirect to login
  if (!currentUser) {
    return <Navigate to="/login" replace />
  }

  // Platform-admin-only routes - send non-platform-admins back home
  if (requirePlatformAdmin && !currentUser.is_platform_admin) {
    return <Navigate to="/" replace />
  }

  // Force a password change before allowing access to any other route
  if (currentUser.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />
  }

  // Clinical profile required routes (must have doctor_id; role can be doctor or admin)
  if (requireClinicalProfile) {
    if (!currentUser.doctor_id) {
      return <Navigate to={getHomeRoute(currentUser)} replace />
    }
    // If allowedRoles is also set, check role too
    if (allowedRoles && !allowedRoles.includes(currentUser.role)) {
      return <Navigate to={getHomeRoute(currentUser)} replace />
    }
    return <>{children}</>
  }

  // Check if role is allowed for this route — redirect home, don't logout
  if (allowedRoles && !allowedRoles.includes(currentUser.role)) {
    return <Navigate to={getHomeRoute(currentUser)} replace />
  }

  return <>{children}</>
}